import type { ServiceType } from './service-types';

/**
 * Service metrics (CLAUDE.md §12–§14; docs/integrations/nextcloud.md §2).
 *
 * A metric is a numeric observation of one service at one time, from a collector (an API
 * integration) or entered manually (CLAUDE.md §13) — the two are always distinguishable. Values are
 * stored exactly as observed; nothing is estimated, interpolated or forecast here.
 *
 * Every metric declares its scope, so application statistics are never mistaken for independent
 * host monitoring:
 *  - `application`: what the application itself reports about its own data (users, files...).
 *  - `host_via_application`: host figures (load, memory, free disk) as the application reads them
 *    from the server it runs on. Useful, but NOT an independent host monitor: if the application is
 *    down, these stop; they cover only what the application can see (e.g. its data volume).
 *  - `object_listing`: derived from a complete listing of a storage bucket's objects (the S3 API
 *    reports no plan capacity, so none is derived).
 *  - `plan`: what the service's plan provides (the Storage Bucket's plan size), entered by the
 *    Admin from the provider's own dashboard — always `manual`, never measured.
 * Independent host monitoring (all volumes, per-process, when the application is down) needs an
 * agent on the server — an open decision (U-8) — and is not represented here.
 */
export const METRIC_UNITS = ['bytes', 'count', 'load'] as const;
export type MetricUnit = (typeof METRIC_UNITS)[number];

export const METRIC_SOURCES = ['collector', 'manual'] as const;
export type MetricSource = (typeof METRIC_SOURCES)[number];

export const METRIC_SCOPES = [
  'application',
  'host_via_application',
  'object_listing',
  'plan',
] as const;
export type MetricScope = (typeof METRIC_SCOPES)[number];

/**
 * Who may read a metric (CLAUDE.md §11, §17), enforced by the API:
 *  - `service`: the service layer (`services.view` — Admin and ICT): usage figures an operator
 *    needs, such as users, files or free space.
 *  - `technical`: server internals (load, memory, swap) — Admin only (`monitoring.view`).
 */
export const METRIC_LAYERS = ['service', 'technical'] as const;
export type MetricLayer = (typeof METRIC_LAYERS)[number];

export interface MetricDefinition {
  serviceType: ServiceType;
  unit: MetricUnit;
  scope: MetricScope;
  layer: MetricLayer;
  label: string;
}

/** The metrics the portal knows. A key not listed here is never stored or shown. */
export const METRICS = {
  'cloud.app.users': {
    serviceType: 'cloud_server',
    unit: 'count',
    scope: 'application',
    layer: 'service',
    label: 'User accounts',
  },
  'cloud.app.files': {
    serviceType: 'cloud_server',
    unit: 'count',
    scope: 'application',
    layer: 'service',
    label: 'Files',
  },
  'cloud.app.active_users_5m': {
    serviceType: 'cloud_server',
    unit: 'count',
    scope: 'application',
    layer: 'service',
    label: 'Active users (last 5 minutes)',
  },
  'cloud.app.active_users_1h': {
    serviceType: 'cloud_server',
    unit: 'count',
    scope: 'application',
    layer: 'service',
    label: 'Active users (last hour)',
  },
  'cloud.app.active_users_24h': {
    serviceType: 'cloud_server',
    unit: 'count',
    scope: 'application',
    layer: 'service',
    label: 'Active users (last 24 hours)',
  },
  'cloud.app.database_size_bytes': {
    serviceType: 'cloud_server',
    unit: 'bytes',
    scope: 'application',
    layer: 'service',
    label: 'Database size',
  },
  'cloud.host.data_free_bytes': {
    serviceType: 'cloud_server',
    unit: 'bytes',
    scope: 'host_via_application',
    layer: 'service',
    label: 'Free space on the data volume',
  },
  'cloud.host.load_1m': {
    serviceType: 'cloud_server',
    unit: 'load',
    scope: 'host_via_application',
    layer: 'technical',
    label: 'System load (1 minute)',
  },
  'cloud.host.load_5m': {
    serviceType: 'cloud_server',
    unit: 'load',
    scope: 'host_via_application',
    layer: 'technical',
    label: 'System load (5 minutes)',
  },
  'cloud.host.load_15m': {
    serviceType: 'cloud_server',
    unit: 'load',
    scope: 'host_via_application',
    layer: 'technical',
    label: 'System load (15 minutes)',
  },
  'cloud.host.memory_total_bytes': {
    serviceType: 'cloud_server',
    unit: 'bytes',
    scope: 'host_via_application',
    layer: 'technical',
    label: 'Memory (total)',
  },
  'cloud.host.memory_available_bytes': {
    serviceType: 'cloud_server',
    unit: 'bytes',
    scope: 'host_via_application',
    layer: 'technical',
    label: 'Memory (available)',
  },
  'cloud.host.swap_total_bytes': {
    serviceType: 'cloud_server',
    unit: 'bytes',
    scope: 'host_via_application',
    layer: 'technical',
    label: 'Swap (total)',
  },
  'cloud.host.swap_free_bytes': {
    serviceType: 'cloud_server',
    unit: 'bytes',
    scope: 'host_via_application',
    layer: 'technical',
    label: 'Swap (free)',
  },
  'storage.bucket.used_bytes': {
    serviceType: 'storage_bucket',
    unit: 'bytes',
    scope: 'object_listing',
    layer: 'service',
    label: 'Space used',
  },
  'storage.bucket.objects': {
    serviceType: 'storage_bucket',
    unit: 'count',
    scope: 'object_listing',
    layer: 'service',
    label: 'Objects stored',
  },
  'storage.bucket.capacity_bytes': {
    serviceType: 'storage_bucket',
    unit: 'bytes',
    scope: 'plan',
    layer: 'service',
    label: 'Plan size',
  },
} as const satisfies Record<string, MetricDefinition>;

export type MetricKey = keyof typeof METRICS;
export const METRIC_KEYS = Object.keys(METRICS) as MetricKey[];

export function isMetricKey(key: string): key is MetricKey {
  return Object.hasOwn(METRICS, key);
}

/**
 * Metrics the Admin may enter by hand (CLAUDE.md §13): values no collector can read. Stored with
 * source `manual`, who entered them and when, and shown as entered — never as measured.
 */
export const MANUAL_METRIC_KEYS = [
  'storage.bucket.capacity_bytes',
] as const satisfies readonly MetricKey[];
export type ManualMetricKey = (typeof MANUAL_METRIC_KEYS)[number];

export function isManualMetricKey(key: string): key is ManualMetricKey {
  return (MANUAL_METRIC_KEYS as readonly string[]).includes(key);
}

/** The largest plan size the Admin can enter: 1,000 TB (binary), far above any real plan. */
export const MAX_PLAN_BYTES = 1000 * 1024 ** 4;

/**
 * Storage space as the storage service's own dashboard writes it (director, 2026-11-02:
 * "360 GB / 2 TB (17.6%)"): binary units (1 GB = 1,073,741,824 bytes) labelled GB/TB, whole
 * gigabytes, terabytes to one decimal place without a trailing ".0". Used for the Storage Bucket so
 * the portal reads exactly like that dashboard.
 */
export function formatStorageSize(bytes: number): string {
  const GB = 1024 ** 3;
  const TB = 1024 ** 4;
  if (bytes >= TB) return `${trimZero((bytes / TB).toFixed(1))} TB`;
  if (bytes >= GB) return `${Math.round(bytes / GB)} GB`;
  const MB = 1024 ** 2;
  return `${Math.round(bytes / MB)} MB`;
}

/**
 * A share of the plan to one decimal place, as that dashboard shows it ("17.6"): the part used, or
 * with `left` the part still free ("82.4"). Over the plan, nothing is left ("0.0").
 */
export function storagePercent(usedBytes: number, planBytes: number, left = false): string {
  if (planBytes <= 0) return '0.0';
  const used = (usedBytes / planBytes) * 100;
  return (left ? Math.max(0, 100 - used) : used).toFixed(1);
}

function trimZero(text: string): string {
  return text.endsWith('.0') ? text.slice(0, -2) : text;
}

/** How often the Cloud Server statistics are collected (pg-boss cron, every 15 minutes). */
export const CLOUD_METRICS_INTERVAL_MINUTES = 15;

/**
 * How often the Storage Bucket is measured (pg-boss cron, hourly). A measurement lists every object,
 * so its cost grows with the object count; hourly until the real count is known
 * (docs/integrations/storage-bucket.md).
 */
export const STORAGE_METRICS_INTERVAL_MINUTES = 60;

/**
 * A collected value is shown as current only while it is recent: older than two collection
 * intervals plus a grace period, it is "out of date" — an old figure is never presented as now.
 */
export function isMetricStale(
  observedAt: Date,
  now: Date,
  intervalMinutes = CLOUD_METRICS_INTERVAL_MINUTES,
): boolean {
  return now.getTime() - observedAt.getTime() > (2 * intervalMinutes + 5) * 60_000;
}
